import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AxSalesBasketResponse } from '@app/shared/models/responses.model';
import { GuidService } from '@app/shared/services/guid.service';
import { FigureConfigService } from '@app/shared/services/internal-services/figureconfig-internal.service';
import { ReclaimApiService } from '@app/shared/services/reclaim-api.service';
import { SalesBasketApiService } from '@app/shared/services/salesbasket-api.service';
import { UserService } from '@app/shared/services/User.Service';
import { DomainContextSelectors } from '@app/shared/state/domain-context/domain-context.selectors';
import { UserContextSelectors } from '@app/shared/state/user-context/user-context.selectors';
import { Store } from '@ngxs/store';
import { finalize, map, of, switchMap, throwError } from 'rxjs';

@Component({
    selector: 'copy-webquotation',
    imports: [FormsModule],
    templateUrl: './copy-webquotation.component.html',
    styleUrl: './copy-webquotation.component.scss'
})
export class CopyWebquotationComponent implements OnInit {

    #store = inject(Store)
    #userInternalService = inject(UserService)
    #figureConfigService = inject(FigureConfigService)
    #reclaimApiService = inject(ReclaimApiService)
    #salesBasketApiService = inject(SalesBasketApiService)

    @Input() copyFromSalesBasketId: string
    @Input() createNewVersion: boolean
    @Input() createReclaim: boolean
    @Input() isReclaim: boolean
    @Input() currentBrand: string
    @Input() salesBasketVersionName: string
    @Input() accessLevel: number
    @Input() extraSales: boolean
    @Input() salesBasketLines: any
    @Input() hasExternalProjectId: boolean

    @Output() copyWebquotationModalEvent = new EventEmitter<{ newSalesBasketId: string, displayConvertMessages: boolean } | undefined>()

    keepExternalProjectId: boolean
    keepPriceCalculation: boolean
    keepAssemblerNotes: boolean
    keepCustomerNotes: boolean
    keepEndCustomer: boolean
    keepAdjustmentItems: boolean
    keepCampaignPersistentFlag: boolean
    keepControlMeasure: boolean
    versionName: string
    moveBookings: boolean

    isWarranty: boolean = false
    showLines = signal<boolean>(false)
    switchOutsideMaterial = ""      // "" = Dont switch material, "SWITCH_TO_ALU" = Switch to aluminium where possible, "SWITCH_TO_WOOD" = Switch to wood where possible
    changeToBrand: string = ""      // "" = Dont change brand,    "SP" = Change to SP where possible, "TR" = Change to TR where possible
    errorMessage: string = ""
    processing = signal<boolean>(false)
    brandImageWidth: number = 50
    brandImageHeight: number = 50
    domain = this.#store.selectSignal(DomainContextSelectors.companyId)
    enableConversion = this.#store.selectSignal(UserContextSelectors.enableConversion)
    usesMontage = signal<boolean>(false)
    isReseller = this.#store.selectSignal(UserContextSelectors.isReseller)
    isMF = signal<boolean>(this.domain() === 'mf')
    isBackOffice = signal<boolean>(false)
    newSalesBasketId: string = ''

    ngOnInit(): void {
        this.isBackOffice.set(this.#userInternalService.isBackoffice())

        this.keepExternalProjectId = this.hasExternalProjectId
        this.keepPriceCalculation = this.keepAssemblerNotes = this.keepCustomerNotes = this.keepControlMeasure = this.moveBookings = this.createNewVersion
        this.keepEndCustomer = this.isReclaim || this.createNewVersion
        this.keepAdjustmentItems = this.keepCampaignPersistentFlag = this.extraSales
        this.versionName = this.createNewVersion ? this.salesBasketVersionName : ''

        this.usesMontage.set(this.isMF())

        if (this.accessLevel < 1 && (this.#userInternalService.isRC_MC() || this.#userInternalService.isBackoffice())) {
            this.accessLevel = 1
        }
    }

    showWarrenty(): boolean {
        return this.isBackOffice() && this.createReclaim && !this.createNewVersion
    }

    showReclaim(): boolean {
        return this.isMF() && this.createReclaim && !this.createNewVersion
    }

    logoSrc(brand: string): string {
        if (brand.toUpperCase() == 'SP') {
            return '/assets/public/images/sp_50x48.png'
        }
        else if (brand.toUpperCase() == 'TR') {
            return '/assets/public/images/traryd_50x50.png'
        }

        return ''
    }

    onKeepExternalProjectIdChange(newValue: boolean, oldValue: boolean) {
        if (newValue !== undefined && newValue !== oldValue) {
            if (newValue && !this.isMF()) {
                this.keepEndCustomer = true
                console.log('keepExternalProjectId WATCH triggered');
            }
        }
    }

    clickCancel() {
        this.copyWebquotationModalEvent.emit(undefined)
    }

    clickOk() {
        this.salesBasketCopyToNew();
    }

    isConverting() {
        return this.processing() && (this.changeToBrand !== '' || this.switchOutsideMaterial != '');
    }

    isUpdatingLineDescriptions() {
        return this.#figureConfigService.isUpdatingLineDescriptions()
    }

    private salesBasketCopyToNew() {

      if (this.isReclaim || this.isWarranty) {
          this.salesBasketCreateReclaim()
          return
      }

      this.processing.set(true)

      this.#salesBasketApiService.copyToNew(
        this.copyFromSalesBasketId,
        this.createNewVersion,
        this.keepEndCustomer,
        this.keepPriceCalculation,
        this.switchOutsideMaterial,
        this.changeToBrand,
        this.versionName,
        this.keepControlMeasure,
        this.moveBookings,
        this.keepAdjustmentItems,
        this.keepCampaignPersistentFlag,
        this.keepExternalProjectId,
        this.keepAssemblerNotes,
        this.keepCustomerNotes
      ).pipe(
        switchMap((axResponse: AxSalesBasketResponse) => {
          if (!axResponse?.AllOk) {
          return throwError(() => new Error("copyToNew failed"))
          }
          if(this.createNewVersion || this.extraSales) return of(axResponse)
          return this.#salesBasketApiService
            .setCrmOpportunityId(GuidService.getEmpty(), axResponse.SalesBasketId)
            .pipe(
              map(() => axResponse)
            );
        }),
        finalize(() => this.processing.set(false))
      ).subscribe({
        next: (axResponse: AxSalesBasketResponse) => {
          if (this.isConverting()) {
            this.updateLineDescriptions(axResponse.SalesBasketId);
          } else {
            this.copyWebquotationModalEvent.emit({
              newSalesBasketId: axResponse.SalesBasketId,
              displayConvertMessages: false
            });
          }
        },
        error: (err:any) => {
          if (err.name === 'TimeoutError') {
            this.errorMessage = "Affärsförslaget håller på att skapas men det tar längre tid än vanligt.\nDu kan stänga den här dialogen och titta i listan på nya affärsförslag.\nDet kan ta en stund innan alla rader har skapats.";
            return
          }
          console.error(err);
          this.errorMessage = this.isConverting()
            ? "Misslyckades konvertera offerten."
            : "Misslyckades kopiera offerten.";
        }
      });
    }

    private salesBasketCreateReclaim() {

        this.processing.set(true)

        const lines: string[] = []

        if (this.salesBasketLines != null) {

          for(const sbl of this.salesBasketLines) {
            const line = sbl
            if (line.lineSelected) {
                lines.push(line.BASKETTRANSID)
            } else {
              for(const ls of line.sublines) {
                const subline = ls
                  if (subline.lineSelected) {
                      lines.push(subline.BASKETTRANSID)
                  }
              }
            }
          }
        }

        this.#reclaimApiService.createReclaim(
            this.copyFromSalesBasketId,
            this.versionName,
            this.isWarranty,
            lines
        )
            .pipe(finalize(() => this.processing.set(false)))
            .subscribe({
                next: (axResponse: AxSalesBasketResponse) => {
                    if (axResponse?.AllOk) {
                        this.copyWebquotationModalEvent.emit({ newSalesBasketId: axResponse.SalesBasketId, displayConvertMessages: false })
                    }
                },
                error: () => {
                    this.errorMessage = "Misslyckades skapa reklamation."
                }
            })
    }

    /**
     * Update salesbasket line descriptions via dataservice/Factory
     */
    private updateLineDescriptions(newSalesBasketId: string): void {

        this.#figureConfigService.isUpdatingLineDescriptions(true)

        this.#salesBasketApiService.updateLineDescriptions(newSalesBasketId, '', false)
            .subscribe({
                next: (axResponse) => {
                    if (axResponse.AllOk) {
                        this.copyWebquotationModalEvent.emit({ newSalesBasketId: newSalesBasketId, displayConvertMessages: true })
                    }
                    else {
                        this.errorMessage = "Misslyckades konvertera offerten."
                    }
                    this.processing.set(false)

                    this.#figureConfigService.isUpdatingLineDescriptions(false)
                },
                error: () => {
                    this.errorMessage = "Misslyckades kopiera offerten."
                    this.processing.set(false)
                    this.#figureConfigService.isUpdatingLineDescriptions(false)
                }
            })
    }
}
